import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    interestedKitten: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4ip0u2eh8g2mrj4areg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          message: '',
          interestedKitten: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-b from-blue-50 to-purple-50">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">Get in Touch With Us</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We're here to answer all your questions and help you bring home the perfect Ragdoll kitten.
          </p>
        </div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="bg-white rounded-3xl p-10 shadow-xl">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Contact Information</h3>
            
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-300 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-mail-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Email</h4>
                  <a 
                    href="mailto:ragdollgentlepawsadoption@gmail.com"
                    className="text-blue-400 hover:text-blue-500 cursor-pointer"
                  >
                    ragdollgentlepawsadoption@gmail.com
                  </a>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-300 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-phone-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Phone</h4>
                  <p className="text-gray-600">727-300-7541</p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-300 rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                  <i className="ri-time-line text-xl text-white"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Response Time</h4>
                  <p className="text-gray-600">We typically respond within 24 hours</p>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-10 border-t border-gray-200">
              <h4 className="font-bold text-gray-900 mb-4">Connect With Us</h4>
              <div className="flex gap-4">
                <a 
                  href="#" 
                  className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-300 rounded-xl flex items-center justify-center hover:shadow-lg transition-all cursor-pointer"
                >
                  <i className="ri-instagram-line text-2xl text-white"></i>
                </a>
                <a 
                  href="#" 
                  className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-300 rounded-xl flex items-center justify-center hover:shadow-lg transition-all cursor-pointer"
                >
                  <i className="ri-facebook-fill text-2xl text-white"></i>
                </a>
                <a 
                  href="#" 
                  className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-300 rounded-xl flex items-center justify-center hover:shadow-lg transition-all cursor-pointer"
                >
                  <i className="ri-tiktok-fill text-2xl text-white"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl p-10 shadow-xl">
            <h3 className="text-3xl font-bold text-gray-900 mb-8">Send a Message</h3>
            
            <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-gray-700 font-semibold mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-700 font-semibold mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-700 font-semibold mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors text-sm"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label htmlFor="interestedKitten" className="block text-gray-700 font-semibold mb-2">
                  Interested Kitten
                </label>
                <input
                  type="text"
                  id="interestedKitten"
                  name="interestedKitten"
                  value={formData.interestedKitten}
                  onChange={(e) => setFormData({...formData, interestedKitten: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors text-sm"
                  placeholder="Luna, Oliver, etc."
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-gray-700 font-semibold mb-2">
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-400 focus:outline-none transition-colors resize-none text-sm"
                  placeholder="Tell us about yourself and why you'd like to adopt a Ragdoll kitten..."
                ></textarea>
                <p className="text-sm text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-400 text-green-700 px-4 py-3 rounded-xl">
                  Thank you! We'll get back to you soon.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl">
                  Something went wrong. Please try again or email us directly.
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="whitespace-nowrap w-full py-4 bg-gradient-to-r from-blue-400 to-purple-300 text-white rounded-full font-semibold text-lg hover:from-blue-500 hover:to-purple-400 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
